import React, { useEffect, useState } from "react";
import config from "../../config";
import { getAuth, signInAnonymously } from "firebase/auth";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "../../firebase";

const RequiredStar = () => (
  <span style={{ color: "#dc2626", marginLeft: 4 }}>*</span>
);

/* ===========================
   IMAGE NORMALIZATION HELPERS
   =========================== */

const IMAGE_CONFIG = {
  aspectRatio: 3 / 4,      // width / height (3:4 portrait)
  maxWidth: 1200,          // we will scale down if larger
  recommendedMinWidth: 900,
  recommendedMinHeight: 1200,
  hardMin: 400,            // below this is considered unusable
  quality: 0.85
};

// Helper: load an image element from a File
function loadImageFromFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// Helper: generate warnings based on original resolution & aspect ratio
function analyzeImageResolution(width, height) {
  const warnings = [];
  const {
    recommendedMinWidth,
    recommendedMinHeight,
    aspectRatio,
    hardMin
  } = IMAGE_CONFIG;

  if (width < hardMin || height < hardMin) {
    warnings.push(
      `Image is extremely small (${width}×${height}). It may look very blurry on mobile.`
    );
    return warnings;
  }

  if (width < recommendedMinWidth || height < recommendedMinHeight) {
    warnings.push(
      `Low resolution (${width}×${height}). Recommended minimum is ` +
      `${recommendedMinWidth}×${recommendedMinHeight} for best clarity.`
    );
  }

  const ratio = width / height;
  const diff = Math.abs(ratio - aspectRatio);

  if (diff > 0.2) {
    if (ratio > aspectRatio) {
      warnings.push(
        'Image is quite wide. Top/bottom parts may be cropped in mobile view.'
      );
    } else {
      warnings.push(
        'Image is quite tall. Sides may be cropped in mobile view.'
      );
    }
  }

  return warnings;
}

/**
 * Phase 1 strategy:
 * - Always output a 3:4 portrait image (full-bleed, Myntra-style).
 * - For wide/square originals -> crop from TOP to preserve head/shoulders.
 * - For taller images -> crop around vertical centre with a small upward bias.
 */
async function normalizeImageFile(file) {
  const img = await loadImageFromFile(file);

  const {
    aspectRatio,
    maxWidth,
    quality,
    hardMin
  } = IMAGE_CONFIG;

  const originalWidth = img.naturalWidth;
  const originalHeight = img.naturalHeight;

  // HARD BLOCK only if totally unusable
  if (originalWidth < hardMin || originalHeight < hardMin) {
    throw new Error(
      `Image is too small (${originalWidth}×${originalHeight}). ` +
      `Please upload a higher resolution image.`
    );
  }

  // Step 1: decide target dimensions with 3:4 ratio
  let targetWidth = Math.min(originalWidth, maxWidth);
  let targetHeight = Math.round(targetWidth / aspectRatio);

  if (targetHeight > originalHeight) {
    targetHeight = originalHeight;
    targetWidth = Math.round(targetHeight * aspectRatio);
  }

  // Step 2: smart crop from original image
  const cropWidth = targetWidth;
  const cropHeight = targetHeight;

  let cropX = Math.round((originalWidth - cropWidth) / 2);
  let cropY;
  const ratio = originalWidth / originalHeight;

  if (ratio >= aspectRatio) {
    // ✅ Wide or square image:
    // Keep TOP of the image to avoid chopping the head.
    cropY = 0;
  } else {
    // ✅ Tall image:
    // Centre vertically with a small upward bias.
    cropY = Math.round((originalHeight - cropHeight) / 2);

    const upwardBias = Math.round(cropHeight * 0.05); // 5% upwards
    cropY = Math.max(0, cropY - upwardBias);
    if (cropY + cropHeight > originalHeight) {
      cropY = originalHeight - cropHeight;
    }
  }

  // Safety clamp
  if (cropX < 0) cropX = 0;
  if (cropX + cropWidth > originalWidth) {
    cropX = originalWidth - cropWidth;
  }
  if (cropY < 0) cropY = 0;
  if (cropY + cropHeight > originalHeight) {
    cropY = originalHeight - cropHeight;
  }

  // Step 3: draw onto canvas
  const canvas = document.createElement('canvas');
  canvas.width = cropWidth;
  canvas.height = cropHeight;
  const ctx = canvas.getContext('2d');

  ctx.drawImage(
    img,
    cropX, cropY, cropWidth, cropHeight, // source rect
    0, 0, cropWidth, cropHeight          // destination rect
  );

  // Step 4: canvas -> Blob (JPEG)
  const blob = await new Promise((resolve) =>
    canvas.toBlob((b) => resolve(b), 'image/jpeg', quality)
  );

  const normalizedFile = new File(
    [blob],
    file.name.replace(/\.(png|jpeg|jpg|webp)$/i, '') + '_aurakart.jpg',
    { type: 'image/jpeg' }
  );

  return {
    normalizedFile,
    normalizedWidth: cropWidth,
    normalizedHeight: cropHeight,
    originalWidth,
    originalHeight
  };
}

// Global color list for product/variants (same as CreateProductForm)
const colorOptions = [
  "Black", "White", "Grey", "Navy Blue", "Blue", "Red", "Maroon", "Pink",
  "Green", "Yellow", "Mustard", "Orange", "Purple", "Brown", "Beige", "Off-White",
  // then the rest...
  "Charcoal", "Cream", "Ivory", "Sky Blue", "Aqua", "Teal", "Turquoise", "Olive",
  "Mint Green", "Pista Green", "Bottle Green", "Sage Green", "Wine", "Burgundy",
  "Coral", "Blush Pink", "Dusty Pink", "Rose", "Hot Pink", "Rust", "Terracotta",
  "Lavender", "Chocolate Brown", "Tan", "Khaki", "Camel", "Multicolor", "Printed"
];


export default function UpdateProduct({ product, onClose, onProductUpdated }) {
  const [currentStep, setCurrentStep] = useState(1);

  const [categories, setCategories] = useState([]); // from backend
  const [subCategories, setSubCategories] = useState([]); // from backend

  const [allSizes, setAllSizes] = useState([]);
  const [availableSizes, setAvailableSizes] = useState([]);
  const [sizesLoading, setSizesLoading] = useState(false);
  const [sizesError, setSizesError] = useState("");

  const sizeOptions = availableSizes.length > 0 ? availableSizes : allSizes;

  const [form, setForm] = useState({
    productName: product.productName || "",
    color: product.color || "",
    productDescription: product.productDescription || "",
    active: product.active ?? true,
    hsnCode: product.hsnCode || "",
    categoryName: product.category?.name || product.categoryName || "",
    subCategoryName:
      product.subCategory?.subCategoryName || product.subCategoryName || "",
    details: { ...(product.details || {}) },
    variants:
      product.variants?.map((v) => ({
        ...v,
        variantId: v.variantId || v.id,
        sizeName: v.size?.code || v.sizeName || v.size?.name || "",
        vendorId: v.vendor?.vendorId || v.vendorId,
        offerPrice:
          v.offerPrice ||
          (parseFloat(v.originalPrice) * (1 - (v.discount || 0) / 100)),
      })) || [],
    images: product.images || [],
  });


  // Colors stored for this product in DB (/api/products/{id}/colors)
  const [productColors, setProductColors] = useState([]);
  const [selectedImageColor, setSelectedImageColor] = useState('');
  const [completedColors, setCompletedColors] = useState([]); // colors for which images uploaded in this session

  // Each entry: null or { file, originalWidth, originalHeight, normalizedWidth, normalizedHeight, warnings[] }
  const [imageViewTypes, setImageViewTypes] = useState({
    'Main View': null,
    'Front View': null,
    'Back View': null,
    'Left Side View': null,
    'Right Side View': null
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  /* ===========================
     FETCH CATEGORIES + SUBCATEGORIES
     =========================== */

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await fetch(`${config.baseUrl}/api/categories`);
        if (res.ok) {
          const data = await res.json();
          setCategories(data || []);
        }
      } catch (err) {
        console.error("Failed to fetch categories:", err);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    if (!form.categoryName) {
      setSubCategories([]);
      return;
    }

    const fetchSubCategories = async () => {
      try {
        const encoded = encodeURIComponent(form.categoryName);
        const res = await fetch(
          `${config.baseUrl}/api/subCategories/category/${encoded}`
        );
        if (res.ok) {
          const data = await res.json();
          setSubCategories(data || []);
        } else {
          setSubCategories([]);
        }
      } catch (err) {
        console.error("Failed to fetch subcategories:", err);
        setSubCategories([]);
      }
    };

    fetchSubCategories();
  }, [form.categoryName]);

  /* ===========================
     FETCH SIZES
     =========================== */

  // all sizes fallback
  useEffect(() => {
    const fetchAllSizes = async () => {
      try {
        const res = await fetch(`${config.baseUrl}/api/sizes`);
        if (res.ok) {
          let data = await res.json();
          data = (data || []).sort(
            (a, b) => (a.displayOrder || 0) - (b.displayOrder || 0)
          );
          setAllSizes(data);
        }
      } catch (err) {
        console.error("Failed to fetch all sizes", err);
      }
    };
    fetchAllSizes();
  }, []);

  // subCategory-specific sizes
  useEffect(() => {
    if (!form.categoryName || !form.subCategoryName) {
      setAvailableSizes([]);
      setSizesError("");
      return;
    }

    const fetchSizesForSubCategory = async () => {
      setSizesLoading(true);
      setSizesError("");
      try {
        const params = new URLSearchParams({
          categoryName: form.categoryName,
          subCategoryName: form.subCategoryName,
        });
        const res = await fetch(
          `${config.baseUrl}/api/subCategorySizes?${params.toString()}`
        );
        if (res.ok) {
          let data = await res.json();
          data = (data || []).sort(
            (a, b) => (a.displayOrder || 0) - (b.displayOrder || 0)
          );
          setAvailableSizes(data);
          if (!data || data.length === 0) {
            setSizesError(
              "No specific sizes configured for this sub-category. Showing all sizes as fallback."
            );
          }
        } else {
          setAvailableSizes([]);
          setSizesError(
            "Unable to load sizes for this sub-category. Showing all sizes as fallback."
          );
        }
      } catch (err) {
        console.error("Failed to fetch subCategory sizes:", err);
        setAvailableSizes([]);
        setSizesError(
          "Failed to load sub-category sizes. Showing all sizes as fallback."
        );
      } finally {
        setSizesLoading(false);
      }
    };

    fetchSizesForSubCategory();
  }, [form.categoryName, form.subCategoryName]);

  // Fetch product colors for Step 4 image management
  useEffect(() => {
    const productId = product.id || product.productId;
    if (!productId) return;

    const fetchProductColors = async () => {
      try {
        const res = await fetch(`${config.baseUrl}/api/products/${productId}/colors`);
        if (!res.ok) return;
        const data = await res.json();
        setProductColors(data || []);
        if (data && data.length > 0 && !selectedImageColor) {
          setSelectedImageColor(data[0].colorName);
        }
      } catch (err) {
        console.error('Failed to fetch product colors:', err);
      }
    };

    fetchProductColors();
  }, [product.id, product.productId]);

  useEffect(() => {
    return () => {
      Object.values(imageViewTypes).forEach(v => {
        if (v?.previewUrl) URL.revokeObjectURL(v.previewUrl);
      });
    };
  }, [imageViewTypes]);

  /* ===========================
     BASIC HANDLERS
     =========================== */
  const handleChange = (field, value) =>
    setForm(prev => ({
      ...prev,
      [field]: value,
      ...(field === "categoryName" ? { subCategoryName: "" } : {})
    }));

  const handleDetailsChange = (field, value) =>
    setForm((prev) => ({
      ...prev,
      details: { ...(prev.details || {}), [field]: value },
    }));

  /* ===========================
     VARIANTS
     =========================== */

  const handleVariantChange = (originalIdx, field, value) => {
    const updated = [...form.variants];

    const newVariant = {
      ...updated[originalIdx],
      [field]: value,
    };

    // � WARNING: Check if changing color that has existing images
    if (field === 'color' && updated[originalIdx].color && updated[originalIdx].color !== value) {
      const oldColor = updated[originalIdx].color;

      // Check if old color has images in the product
      const hasImages = form.images.some(img =>
        (img.color === oldColor) ||
        productColors.some(pc => pc.colorName === oldColor)
      );

      if (hasImages) {
        const confirmed = window.confirm(
          `Warning: The color "${oldColor}" already has images uploaded. \n\n` +
          `If you change to "${value}", existing images will not move automatically. \n\n` +
          `You may need to:\n` +
          `• Re-upload images for the new color\n` +
          `• Or manually manage image colors in Step 4\n\n` +
          `Do you want to continue with this color change?`
        );

        if (!confirmed) {
          return; // Cancel the change
        }
      }
    }

    // �🔒 Prevent duplicate SIZE + COLOR combination
    if (newVariant.sizeName && newVariant.color) {
      const exists = updated.some(
        (v, i) =>
          i !== originalIdx &&
          v.sizeName === newVariant.sizeName &&
          v.color === newVariant.color
      );

      if (exists) {
        alert("This size & color combination already exists.");
        return;
      }
    }

    updated[originalIdx] = newVariant;

    // Auto-calculate offer price
    if (field === "discount" || field === "originalPrice") {
      const originalPrice = parseFloat(newVariant.originalPrice) || 0;
      const discount = parseFloat(newVariant.discount) || 0;

      updated[originalIdx].offerPrice =
        originalPrice > 0
          ? Math.round(originalPrice * (1 - discount / 100))
          : "";
    }

    setForm((prev) => ({ ...prev, variants: updated }));
  };

  const handleRemoveVariant = (idx) => {
    setForm((prev) => ({
      ...prev,
      variants: prev.variants.filter((_, i) => i !== idx),
    }));
  };

  const handleAddVariant = () => {
    setForm((prev) => ({
      ...prev,
      variants: [
        ...prev.variants,
        {
          variantId: null,     // 👈 VERY IMPORTANT
          sku: "",
          sizeName: "",
          originalPrice: "",
          discount: "",
          offerPrice: "",
          stockQuantity: "",
          color: "",
          isNew: true          // 👈 Optional but very helpful
        },
      ],
    }));
  };
  /* ===========================
     IMAGES (NEW UPLOADS)
     =========================== */

  const handleImageUploadForView = async (viewType, file) => {
    if (!file) return;

    try {
      const {
        normalizedFile,
        normalizedWidth,
        normalizedHeight,
        originalWidth,
        originalHeight
      } = await normalizeImageFile(file);

      const warnings = analyzeImageResolution(originalWidth, originalHeight);

      setImageViewTypes(prev => ({
        ...prev,
        [viewType]: {
          file: normalizedFile,
          previewUrl: URL.createObjectURL(normalizedFile), // ✅ add
          originalWidth,
          originalHeight,
          normalizedWidth,
          normalizedHeight,
          warnings
        }
      }));
    } catch (err) {
      console.error('Image processing failed:', err);
      setError(
        err.message ||
        'Failed to process image. Please try with a higher resolution image.'
      );
    }
  };

  const removeImageFromView = (viewType) => {
    setImageViewTypes(prev => ({
      ...prev,
      [viewType]: null
    }));
  };

  /* ===========================
     STEP NAVIGATION
     =========================== */

  const goToNextStep = () => setCurrentStep((prev) => Math.min(prev + 1, 4));
  const goToPrevStep = () => setCurrentStep((prev) => Math.max(prev - 1, 1));

  /* ===========================
     STEP 3: SAVE PRODUCT (NO IMAGES)
     =========================== */

  const handleSaveProductAtStep3 = async () => {
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const productId = product.id || product.productId;
      if (!productId) {
        throw new Error("Product ID missing.");
      }

      // Basic validation: same as CreateProductForm
      if (
        !form.productName ||
        !form.categoryName ||
        !form.subCategoryName ||
        !form.hsnCode
      ) {
        throw new Error(
          "Please fill all required fields: Name, Category, Sub Category, HSN Code."
        );
      }

      const validVariants = form.variants.filter(
        (v) => v.sizeName && v.color && v.sku && v.originalPrice !== ""
      );

      if (validVariants.length === 0) {
        throw new Error("Please add at least one complete variant with Size, Color, SKU, and Original Price.");
      }

      if (validVariants.length !== form.variants.length) {
        throw new Error(
          'Please complete all variant fields (Size, Color, SKU, Original Price) or remove unfinished variants.'
        );
      }

      const payload = {
        productName: form.productName,
        productDescription: form.productDescription,
        active: form.active,
        categoryName: form.categoryName,
        subCategoryName: form.subCategoryName,
        hsnCode: form.hsnCode,
        details: form.details || {},
        variants: validVariants.map((v) => ({
          variantId: v.variantId,
          sku: v.sku,
          sizeName: v.sizeName,
          originalPrice: parseFloat(v.originalPrice),
          discount: parseFloat(v.discount || 0),
          offerPrice: Math.round(parseFloat(v.offerPrice || 0)),
          stockQuantity: parseInt(v.stockQuantity || 0, 10),
          vendorId: v.vendorId,
          color: v.color || null,   // ✅ ONLY variant color
        })),

        // Images are managed in Step 4 via separate endpoint
        images: [],
      };

      const response = await fetch(
        `${config.baseUrl}/api/products/${productId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Failed to update product");
      }

      setSuccess(
        "Product details and variants updated successfully! Now you can manage images."
      );
      setTimeout(() => {
        goToNextStep();
        setSuccess("");
      }, 800);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  /* ===========================
     STEP 4: SAVE NEW IMAGES (OPTIONAL)
     =========================== */

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const productId = product.id || product.productId;
      if (!productId) {
        throw new Error("Product ID missing.");
      }

      if (productColors.length > 0 && !selectedImageColor) {
        throw new Error('Please select a color for these images.');
      }

      const allImages = Object.entries(imageViewTypes)
        .filter(([_, info]) => info && info.file)
        .map(([viewType, info]) => ({ viewType, file: info.file }));

      let uploadedImagesPayload = [];

      if (allImages.length > 0) {
        const auth = getAuth();
        if (!auth.currentUser) {
          await signInAnonymously(auth);
        }

        const userId = auth.currentUser.uid;
        const timestampBase = Date.now();

        const uploaded = await Promise.all(
          allImages.map(async ({ viewType, file }, idx) => {
            const filename = `updated_${timestampBase}_${idx}.jpg`;
            const storageRef = ref(
              storage,
              `product-images/${userId}/${productId}/${filename}`
            );

            await uploadBytes(storageRef, file);
            const url = await getDownloadURL(storageRef);

            return {
              imageUrl: url,
              imageType: viewType,
              displayOrder: idx + 1,
              color: selectedImageColor || null,
            };
          })
        );

        uploadedImagesPayload = uploaded;

        const imageResponse = await fetch(
          `${config.baseUrl}/api/productimages/product/${productId}`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(uploadedImagesPayload),
          }
        );

        if (!imageResponse.ok) {
          const errorData = await imageResponse.json().catch(() => ({}));
          console.warn(
            "Warning: Images saved to Firebase but backend upload had issues:",
            errorData
          );
        }

        const label = selectedImageColor || 'Default';
        if (label && !completedColors.includes(label)) {
          setCompletedColors(prev => [...prev, label]);
        }

        // Clear uploaded images for next color
        setImageViewTypes({
          'Main View': null,
          'Front View': null,
          'Back View': null,
          'Left Side View': null,
          'Right Side View': null
        });
      }

      let msg = "Product updated successfully.";
      if (uploadedImagesPayload.length > 0) {
        const colorText = selectedImageColor ? ` for ${selectedImageColor}` : '';
        msg = `Images uploaded successfully${colorText}. You can switch to another color and upload again, or close to finish.`;
      } else {
        msg += " No new images uploaded.";
      }

      setSuccess(msg);
      if (uploadedImagesPayload.length === 0) {
        setTimeout(() => {
          onProductUpdated && onProductUpdated();
          onClose && onClose();
        }, 1000);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  /* ===========================
     RENDER
     =========================== */

  return (
    <div className="modal-overlay">
      <form className="modal-content large-modal" onSubmit={handleSubmit}>
        <div className="modal-header">
          <h2>Update Product</h2>
          <div className="step-indicator">
            {[1, 2, 3, 4].map((step) => (
              <span
                key={step}
                className={`${step === currentStep ? "active" : ""} ${step < currentStep ? "completed" : ""
                  }`}
                onClick={() => setCurrentStep(step)}
                style={{ cursor: "pointer" }}
              >
                {step}
              </span>
            ))}
          </div>
          <button type="button" className="close-btn" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="modal-body">
          {error && <div className="error-message">{error}</div>}
          {success && <div className="success-message">{success}</div>}

          {/* Step 1: Basic Info */}
          {currentStep === 1 && (
            <div className="form-section">
              <h3>Step 1: Basic Product Information</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label>
                    Product Name <RequiredStar />
                  </label>
                  <input
                    value={form.productName}
                    onChange={(e) =>
                      handleChange("productName", e.target.value)
                    }
                    required
                  />
                </div>

                <div className="form-group">
                  <label>
                    Category <RequiredStar />
                  </label>
                  <select
                    value={form.categoryName}
                    onChange={(e) =>
                      handleChange("categoryName", e.target.value)
                    }
                    required
                  >
                    <option value="">Select Category</option>
                    {categories.map((cat) => (
                      <option key={cat.categoryId} value={cat.name}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>
                    Sub Category <RequiredStar />
                  </label>
                  <select
                    value={form.subCategoryName}
                    onChange={(e) =>
                      handleChange("subCategoryName", e.target.value)
                    }
                    disabled={!form.categoryName}
                    required
                  >
                    <option value="">Select Sub Category</option>
                    {subCategories.map((sc) => (
                      <option key={sc.subCategoryId} value={sc.subCategoryName}>
                        {sc.subCategoryName}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>
                    HSN Code <RequiredStar />
                  </label>
                  <input
                    value={form.hsnCode}
                    onChange={(e) =>
                      handleChange("hsnCode", e.target.value)
                    }
                    placeholder="e.g., 6109"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Rating</label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    max="5"
                    value={form.rating}
                    onChange={(e) =>
                      handleChange("rating", e.target.value)
                    }
                    placeholder="0.0 to 5.0"
                  />
                </div>

                <div className="form-group">
                  <label>Active</label>
                  <input
                    type="checkbox"
                    checked={form.active}
                    onChange={(e) =>
                      handleChange("active", e.target.checked)
                    }
                  />
                </div>

                <div className="form-group full-width">
                  <label>Product Description</label>
                  <textarea
                    value={form.productDescription}
                    onChange={(e) =>
                      handleChange("productDescription", e.target.value)
                    }
                    rows={3}
                  />
                </div>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn-primary"
                  onClick={goToNextStep}
                >
                  Next: Details
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Additional Details */}
          {currentStep === 2 && (
            <div className="form-section">
              <h3>Step 2: Additional Product Details</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label>Fabric</label>
                  <input
                    value={form.details.fabric || ""}
                    onChange={(e) =>
                      handleDetailsChange("fabric", e.target.value)
                    }
                  />
                </div>
                <div className="form-group">
                  <label>Pattern</label>
                  <input
                    value={form.details.pattern || ""}
                    onChange={(e) =>
                      handleDetailsChange("pattern", e.target.value)
                    }
                  />
                </div>
                <div className="form-group">
                  <label>Neck Type</label>
                  <input
                    value={form.details.neck || ""}
                    onChange={(e) =>
                      handleDetailsChange("neck", e.target.value)
                    }
                  />
                </div>
                <div className="form-group">
                  <label>Sleeve Length</label>
                  <input
                    value={form.details.sleeveLength || ""}
                    onChange={(e) =>
                      handleDetailsChange("sleeveLength", e.target.value)
                    }
                  />
                </div>
                <div className="form-group">
                  <label>Fit</label>
                  <input
                    value={form.details.fit || ""}
                    onChange={(e) =>
                      handleDetailsChange("fit", e.target.value)
                    }
                  />
                </div>
                <div className="form-group">
                  <label>Occasion</label>
                  <input
                    value={form.details.occasion || ""}
                    onChange={(e) =>
                      handleDetailsChange("occasion", e.target.value)
                    }
                  />
                </div>
                <div className="form-group">
                  <label>Brand</label>
                  <input
                    value={form.details.brand || ""}
                    onChange={(e) =>
                      handleDetailsChange("brand", e.target.value)
                    }
                  />
                </div>
                <div className="form-group">
                  <label>Country of Origin</label>
                  <input
                    value={form.details.countryOfOrigin || ""}
                    onChange={(e) =>
                      handleDetailsChange(
                        "countryOfOrigin",
                        e.target.value
                      )
                    }
                  />
                </div>
                <div className="form-group">
                  <label>Material</label>
                  <input
                    value={form.details.material || ""}
                    onChange={(e) =>
                      handleDetailsChange("material", e.target.value)
                    }
                  />
                </div>
                <div className="form-group">
                  <label>Care Instructions</label>
                  <input
                    value={form.details.careInstructions || ""}
                    onChange={(e) =>
                      handleDetailsChange(
                        "careInstructions",
                        e.target.value
                      )
                    }
                  />
                </div>
                <div className="form-group">
                  <label>Features</label>
                  <input
                    value={form.details.features || ""}
                    onChange={(e) =>
                      handleDetailsChange("features", e.target.value)
                    }
                  />
                </div>
                <div className="form-group">
                  <label>Season</label>
                  <input
                    value={form.details.season || ""}
                    onChange={(e) =>
                      handleDetailsChange("season", e.target.value)
                    }
                  />
                </div>
              </div>

              <div className="form-actions">
                <button type="button" onClick={goToPrevStep}>
                  Back
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={goToNextStep}
                >
                  Next: Variants & Pricing
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Variants & Pricing */}
          {currentStep === 3 && (
            <div className="form-section">
              <h3>Step 3: Variants, SKU & Pricing</h3>
              <p className="step-description">
                Configure variants with pricing information. Click{" "}
                <b>Save Product &amp; Next</b> to update and move to images.
              </p>

              {sizesLoading && (
                <div
                  className="error-message"
                  style={{
                    background: "#dbeafe",
                    color: "#1d4ed8",
                    borderColor: "#bfdbfe",
                  }}
                >
                  Loading sizes for this sub-category…
                </div>
              )}
              {sizesError && (
                <div className="error-message" style={{ marginBottom: 10 }}>
                  {sizesError}
                </div>
              )}

              <div className="variants-container">
                {form.variants
                  .map((variant, originalIdx) => ({ variant, originalIdx }))
                  .map(({ variant, originalIdx }, idx) => (
                    <div key={originalIdx} className="variant-card">
                      <div className="variant-header">
                        <h5>
                          Variant {idx + 1}
                          {variant.color && (
                            <span style={{ marginLeft: 8, color: "#555" }}>
                              ({variant.color})
                            </span>
                          )}
                        </h5>

                        {!variant.variantId && (
                          <button
                            type="button"
                            className="btn-remove"
                            onClick={() => handleRemoveVariant(originalIdx)}
                          >
                            Remove
                          </button>
                        )}

                      </div>
                      <div className="variant-grid">
                        <div className="form-group">
                          <label>
                            Size <RequiredStar />
                          </label>
                          <select
                            value={variant.sizeName || ""}
                            onChange={(e) =>
                              handleVariantChange(
                                originalIdx,
                                "sizeName",
                                e.target.value
                              )
                            }
                            required
                            disabled={sizeOptions.length === 0}
                          >
                            <option value="">Select Size</option>
                            {sizeOptions.map((size) => (
                              <option key={size.sizeId} value={size.code}>
                                {size.code}
                                {size.name ? ` - ${size.name}` : ""}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="form-group">
                          <label>
                            Color <RequiredStar />
                          </label>
                          <select
                            value={variant.color || ""}
                            onChange={(e) =>
                              handleVariantChange(originalIdx, "color", e.target.value)
                            }
                            required
                          >
                            <option value="">Select Color *</option>
                            {colorOptions.map(color => (
                              <option key={color} value={color}>
                                {color}
                              </option>
                            ))}
                          </select>
                          <small className="helper-text">
                            Required — used for filtering and display
                          </small>
                        </div>

                        <div className="form-group">
                          <label>SKU <RequiredStar /></label>
                          <input
                            value={variant.sku || ""}
                            onChange={(e) =>
                              handleVariantChange(originalIdx, "sku", e.target.value)
                            }
                            placeholder="Product SKU"
                            required
                          />
                        </div>
                        <div className="form-group">
                          <label>
                            Original Price <RequiredStar />
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            value={variant.originalPrice}
                            onChange={(e) =>
                              handleVariantChange(
                                originalIdx,
                                "originalPrice",
                                e.target.value
                              )
                            }
                            placeholder="0.00"
                            required
                          />
                        </div>
                        <div className="form-group">
                          <label>Discount (%)</label>
                          <input
                            type="number"
                            min="0"
                            max="100"
                            value={variant.discount || ""}
                            onChange={(e) =>
                              handleVariantChange(
                                originalIdx,
                                "discount",
                                e.target.value
                              )
                            }
                            placeholder="0"
                          />
                        </div>
                        <div className="form-group">
                          <label>Offer Price</label>
                          <input
                            type="number"
                            step="0.01"
                            value={variant.offerPrice || ""}
                            readOnly
                            className="readonly-field"
                          />
                        </div>
                        <div className="form-group">
                          <label>
                            Stock Quantity <RequiredStar />
                          </label>
                          <input
                            type="number"
                            min="0"
                            value={variant.stockQuantity}
                            onChange={(e) =>
                              handleVariantChange(
                                originalIdx,
                                "stockQuantity",
                                e.target.value
                              )
                            }
                            placeholder="0"
                            required
                          />
                        </div>
                      </div>
                    </div>
                  ))}

                <button
                  type="button"
                  className="btn-add-variant"
                  onClick={handleAddVariant}
                >
                  + Add Another Variant
                </button>
              </div>

              <div className="form-actions">
                <button type="button" onClick={goToPrevStep}>
                  Back
                </button>
                <button
                  type="button"
                  onClick={handleSaveProductAtStep3}
                  disabled={loading}
                  className="btn-primary"
                >
                  {loading ? "Saving Product..." : "Save Product & Next"}
                </button>
              </div>
            </div>
          )}

          {/* Step 4: Images */}
          {currentStep === 4 && (
            <div className="form-section">
              <h3>Step 4: Product Images</h3>
              <p className="step-description">
                Upload high-quality images for each color. We&apos;ll show you a preview of how it
                will look on mobile (3:4 portrait). If the resolution or ratio is not ideal,
                we&apos;ll warn you but won&apos;t stop you.
              </p>

              <div>
                <h4>Existing Images</h4>
                {form.images && form.images.length > 0 ? (
                  <div className="product-images-grid">
                    {form.images.map((img, idx) => (
                      <div key={idx} className="image-item">
                        <img
                          src={img.image_url || img.imageUrl}
                          alt={`Product ${idx}`}
                          loading="lazy"
                        />
                        <div className="image-info">
                          <small className="image-type">
                            {img.image_type || img.imageType || "Image"}
                          </small>
                          {img.color && (
                            <small className="image-color" style={{ display: 'block', marginTop: '2px', fontWeight: 'bold' }}>
                              Color: {img.color}
                            </small>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p>No existing images</p>
                )}
              </div>

              {productColors.length > 0 && (
                <div className="image-status-notice" style={{ marginTop: '20px', marginBottom: '12px' }}>
                  <strong>Note:</strong> Only product colors are listed below. Make sure you upload
                  at least one image for every color you created in Step 3.
                </div>
              )}

              {completedColors.length > 0 && (
                <div style={{ marginBottom: 12, fontSize: '0.85rem' }}>
                  <strong>Colors completed in this session:</strong>{' '}
                  {completedColors.map(c => (
                    <span
                      key={c}
                      style={{
                        display: 'inline-block',
                        padding: '2px 8px',
                        marginRight: 6,
                        marginTop: 4,
                        borderRadius: 12,
                        border: '1px solid #d1d5db',
                        background: '#f3f4f6'
                      }}
                    >
                      {c}
                    </span>
                  ))}
                </div>
              )}

              {productColors.length > 0 ? (
                <div className="form-group" style={{ maxWidth: 320, marginBottom: 18 }}>
                  <label>
                    Image Color <RequiredStar />
                  </label>
                  <select
                    value={selectedImageColor}
                    onChange={(e) => setSelectedImageColor(e.target.value)}
                  >
                    <option value="">Select Color</option>
                    {productColors.map(pc => (
                      <option key={pc.id} value={pc.colorName}>
                        {pc.colorName}
                      </option>
                    ))}
                  </select>
                  <small className="helper-text">
                    Only product colors are listed here. Upload images for one color at a time.
                  </small>
                </div>
              ) : (
                <div className="form-group" style={{ marginBottom: 18 }}>
                  <label>Image Color</label>
                  <p style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                    No product-level colors found. Images will be saved without color grouping.
                  </p>
                </div>
              )}

              <div style={{ marginTop: "24px" }}>
                <h4>Upload New Images for Selected Color</h4>
                <div className="image-views-grid">
                  {Object.keys(imageViewTypes).map((viewType) => {
                    const info = imageViewTypes[viewType];
                    return (
                      <div key={viewType} className="image-view-card">
                        <h5>{viewType}</h5>
                        <label className="upload-label">
                          <input
                            type="file"
                            accept="image/*"
                            style={{ display: 'none' }}
                            onChange={async (e) =>
                              await handleImageUploadForView(
                                viewType,
                                e.target.files && e.target.files[0]
                              )
                            }
                          />
                          <div className="upload-icon">📷</div>
                          <div className="upload-text">Click to upload</div>
                        </label>

                        {info && info.file && (
                          <div style={{ marginTop: 12, position: 'relative' }}>
                            <div className="image-mobile-preview-frame">
                              <img
                                src={URL.createObjectURL(info.file)}
                                alt={viewType}
                              />
                            </div>

                            <div className="image-resolution-info">
                              <div>
                                Original: {info.originalWidth}×{info.originalHeight}px
                              </div>
                              <div>
                                Displayed as: {info.normalizedWidth}×{info.normalizedHeight}px (3:4)
                              </div>

                              {info.warnings && info.warnings.length > 0 && (
                                <div className="image-warning-box">
                                  <strong>Heads up:</strong>
                                  <ul>
                                    {info.warnings.map((w, i) => (
                                      <li key={i}>{w}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </div>

                            <button
                              type="button"
                              className="remove-image"
                              onClick={() => removeImageFromView(viewType)}
                            >
                              ×
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="form-actions">
                <button type="button" onClick={goToPrevStep}>
                  Back
                </button>
                <button
                  type="button"
                  onClick={() => {
                    onProductUpdated && onProductUpdated();
                    onClose && onClose();
                  }}
                >
                  Finish Product &amp; Close
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={loading}
                >
                  {loading ? "Uploading Images..." : "Save Images for Selected Color"}
                </button>
              </div>

              <p style={{ marginTop: 8, fontSize: '0.8rem', color: '#6b7280' }}>
                Tip: After you finish uploading images for all colors, click&nbsp;
                <strong>&quot;Finish Product &amp; Close&quot;</strong> to exit without creating
                duplicates.
              </p>
            </div>
          )}
        </div>
      </form>
    </div>
  );
}
